import { z } from 'zod';

import { ApiError } from '../api/client';
import { CONTACT_ROLES, type ContactRole } from './api';

export const CORPORATE_SITE_VALUE = 'corporate';

/**
 * Mirrors `apps/api` `createContactSchema` (`customer-contact.dto.ts`): role
 * required with no default (`customer-contacts/spec.md` → "Selector de rol
 * obligatorio en el alta"), email optional but validated when present. The
 * email check is a separate `.superRefine()` branch, not folded into the
 * base `z.string().email()`, so an empty string is a valid "not provided"
 * state instead of a validation error.
 */
export const contactFormSchema = z
  .object({
    fullName: z.string().trim().min(1, 'El nombre completo es obligatorio').max(200),
    role: z.enum(CONTACT_ROLES, { message: 'Seleccioná un rol' }),
    email: z.string().trim().max(320),
    phone: z.string().trim().max(50),
    receivesNotifications: z.boolean(),
    siteId: z.string().trim().min(1),
  })
  .superRefine((data, ctx) => {
    if (data.email !== '' && !z.string().email().safeParse(data.email).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El email no es válido',
        path: ['email'],
      });
    }
  });

export type ContactFormValues = z.infer<typeof contactFormSchema>;

/**
 * Raw, unvalidated shape bound to the inputs: `role` allows the empty-string
 * "nothing selected" state a real `<select>` starts in, which
 * `ContactFormValues` (the schema's *output* type) deliberately excludes,
 * same convention as `CustomerFormFields`/`taxCondition`.
 */
export interface ContactFormFields {
  fullName: string;
  role: ContactRole | '';
  email: string;
  phone: string;
  receivesNotifications: boolean;
  siteId: string;
}

export const DEFAULT_CONTACT_FORM_VALUES: ContactFormFields = {
  fullName: '',
  role: '',
  email: '',
  phone: '',
  receivesNotifications: true,
  siteId: CORPORATE_SITE_VALUE,
};

export type ContactFieldErrorTarget = 'siteId';

/**
 * Server errors never have their `message` parsed: the `code` field on
 * 400/403 bodies (`CustomersExceptionFilter`) drives the mapping, same
 * convention as `mapCreateError`/`mapSiteError`.
 */
export function mapContactFormError(error: unknown): {
  formError?: string;
  fieldError?: { field: ContactFieldErrorTarget; message: string };
} {
  if (error === undefined || error === null) {
    return {};
  }

  if (!(error instanceof ApiError)) {
    return { formError: 'Ocurrió un error de conexión. Intentá de nuevo.' };
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 400 && code === 'SITE_FROM_DIFFERENT_CUSTOMER') {
    return {
      fieldError: {
        field: 'siteId',
        message: 'Esa sede no pertenece a esta empresa. Se actualizó la lista de sedes.',
      },
    };
  }

  if (error.status === 403 && code === 'CONTACT_SCOPE_RESTRICTED') {
    return { formError: 'No tenés permiso para asignar ese contacto a esa sede.' };
  }

  if (error.status === 403) {
    return { formError: 'No tenés permiso para realizar esta acción.' };
  }

  if (error.status === 404) {
    return { formError: 'El contacto ya no existe.' };
  }

  if (error.status === 400) {
    return { formError: 'Revisá los datos ingresados e intentá de nuevo.' };
  }

  return { formError: 'Ocurrió un error inesperado. Intentá de nuevo.' };
}

/** Action-level errors (delete, set-default-less list actions), same shape as `mapSiteError`. */
export function mapContactActionError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 403 && code === 'CONTACT_SCOPE_RESTRICTED') {
    return 'No tenés permiso para realizar esta acción sobre este contacto.';
  }

  if (error.status === 404) {
    return 'El contacto ya no existe.';
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/** Contacts refetch on 404 (contact gone), same convention as `shouldRefetchOnError` for sites. */
export function shouldRefetchContactsOnError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** Sites refetch on `SITE_FROM_DIFFERENT_CUSTOMER` (`design.md` §6.2, decision 2). */
export function shouldRefetchSitesOnError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  const code = (error.body as { code?: string } | null)?.code;

  return error.status === 400 && code === 'SITE_FROM_DIFFERENT_CUSTOMER';
}
